import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, CalendarDays, MapPin, Sparkles, Globe2, Check, Camera, Award } from "lucide-react";
import { conference, stats, topics, committee, heroImage, mumbaiSkyline } from "../mock";
import { useReveal, useCountUp } from "../hooks/useReveal";
import { openPhotoGallery, PHOTO_LINKS } from "../lib/utils";
import HeroCanvas from "../components/HeroCanvas";

function useCountdown(target) {
  const calc = () => {
    const diff = new Date(target).getTime() - Date.now();
    return {
      d: Math.max(0, Math.floor(diff / 86400000)),
      h: Math.max(0, Math.floor((diff % 86400000) / 3600000)),
      m: Math.max(0, Math.floor((diff % 3600000) / 60000)),
      s: Math.max(0, Math.floor((diff % 60000) / 1000)),
    };
  };
  const [t, setT] = useState(calc());
  useEffect(() => {
    const id = setInterval(() => setT(calc()), 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line
  }, []);
  return t;
}

const StatCard = ({ value, suffix, label, active }) => {
  const n = useCountUp(value, active);
  return (
    <div className="text-center">
      <div className="font-display font-extrabold text-4xl md:text-5xl text-navy-800 tabular-nums">
        {n.toLocaleString()}<span className="text-brand">{suffix}</span>
      </div>
      <div className="mt-2 text-sm text-slate-500 font-medium">{label}</div>
    </div>
  );
};

const Home = () => {
  useReveal();
  const c = useCountdown(conference.startDate);
  const units = [{ v: c.d, l: "Days" }, { v: c.h, l: "Hours" }, { v: c.m, l: "Mins" }, { v: c.s, l: "Secs" }];
  const statRef = useRef(null);
  const [statActive, setStatActive] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => e.isIntersecting && setStatActive(true), { threshold: 0.3 });
    if (statRef.current) obs.observe(statRef.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center overflow-hidden gradient-navy scene-3d">
        {/* 3D particle-depth canvas */}
        <HeroCanvas />
        {/* perspective grid floor for depth */}
        <div className="absolute bottom-0 left-0 right-0 h-1/3 [transform:perspective(420px)_rotateX(58deg)] origin-bottom opacity-50">
          <div className="w-full h-full grid-floor" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-navy-900/40 via-transparent to-navy-900 pointer-events-none" />
        <div className="absolute -top-24 -right-24 w-96 h-96 bg-brand/20 rounded-full blur-3xl animate-floaty pointer-events-none" />
        <div className="absolute bottom-0 left-10 w-72 h-72 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 py-20 w-full grid lg:grid-cols-12 gap-10 items-center">
          {/* LEFT: content */}
          <div className="lg:col-span-7 order-2 lg:order-1">
            <h1 className="font-display font-extrabold text-white text-4xl md:text-6xl leading-[1.08] tracking-tight">
              {conference.themeLead}{" "}
              <span className="gradient-future">{conference.themeWord}</span>
              <br className="hidden md:block" /> {conference.themeTail}
            </h1>
            <p className="mt-5 text-lg md:text-xl text-slate-300 max-w-2xl leading-relaxed">
              <span className="font-semibold text-white">DRS 2026</span> — Theme: Treating Diabesity, Preventing Complications
            </p>

            <div className="mt-7 flex flex-wrap gap-4">
              <div className="flex items-center gap-2.5 text-white">
                <span className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center"><CalendarDays className="w-5 h-5 text-brand" /></span>
                <div className="leading-tight"><div className="font-semibold">{conference.datesShort}</div><div className="text-xs text-slate-400">Three days of science</div></div>
              </div>
              <div className="flex items-center gap-2.5 text-white">
                <span className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center"><MapPin className="w-5 h-5 text-brand" /></span>
                <div className="leading-tight"><div className="font-semibold">{conference.venue}</div><div className="text-xs text-slate-400">{conference.country}</div></div>
              </div>
            </div>

            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href={PHOTO_LINKS.web}
                onClick={(e) => { e.preventDefault(); openPhotoGallery(); }}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-shine inline-flex items-center bg-brand hover:bg-brand-600 text-white font-semibold rounded-full px-7 h-12 text-base shadow-xl shadow-brand/30 transition-colors"
              >
                <Camera className="w-4 h-4 mr-2" /> Get Your Photos
              </a>
              <a
                href="https://www.certificate.rx-events.live/certificate/drs-2026"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center rounded-full px-7 h-12 text-base border border-white/30 bg-white/5 text-white hover:bg-white/15 transition-colors"
              >
                <Award className="w-4 h-4 mr-2" /> Download Certificate
              </a>
            </div>

            {/* Countdown */}
            <div className="mt-10 grid grid-cols-4 gap-3 max-w-md">
              {units.map((u) => (
                <div key={u.l} className="glass rounded-2xl py-3.5 text-center">
                  <div className="font-display font-extrabold text-2xl md:text-3xl text-white tabular-nums">{String(u.v).padStart(2, "0")}</div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-400 mt-1">{u.l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: Structured Logo Showcase */}
          <div className="lg:col-span-5 flex flex-col gap-6 justify-center items-center order-1 lg:order-2 w-full mb-8 lg:mb-0">
            {/* Main DRS + Partner Card */}
            <div className="bg-white rounded-3xl shadow-2xl shadow-black/40 p-6 sm:p-8 w-full max-w-[420px] flex items-center justify-center transform transition-transform duration-300 hover:scale-[1.02]">
              <img src="/brand/hero-imge.png" alt="DRS 2026" className="w-full h-auto object-contain" />
            </div>
            
            {/* Supporting Logos Row */}
            <div className="hidden lg:flex gap-5 w-full max-w-[420px]">
              {/* Organised by */}
              <div className="flex-1 bg-white rounded-2xl shadow-xl px-5 py-6 flex flex-col items-center justify-start transform transition-transform duration-300 hover:-translate-y-1">
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 mb-3 text-center">Organised by</div>
                <div className="flex-1 flex items-center justify-center">
                  <img src="/brand/mdcf.png" alt="Mumbai Diabetes Care Foundation" className="h-28 w-auto object-contain" />
                </div>
              </div>
              
              {/* Endorsed by */}
              <div className="flex-1 bg-white rounded-2xl shadow-xl px-5 py-6 flex flex-col items-center justify-start transform transition-transform duration-300 hover:-translate-y-1">
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-400 mb-3 text-center">Endorsed by</div>
                <div className="flex-1 flex items-center justify-center">
                  <img src="/brand/idf.png" alt="International Diabetes Federation" className="h-12 w-auto object-contain" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ORGANISERS STRIP */}
      <section className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-6 flex flex-col sm:flex-row items-center justify-center gap-x-12 gap-y-4">
          <div className="flex items-center gap-3">
            <img src="/brand/mdcf.png" alt="Mumbai Diabetes Care Foundation" className="h-16 w-16 object-contain" />
            <div className="leading-tight">
              <div className="text-[11px] uppercase tracking-wider text-slate-400">Organised by</div>
              <div className="font-semibold text-navy-900">{conference.organisedBy}</div>
            </div>
          </div>
          <div className="hidden sm:block w-px h-10 bg-slate-200" />
          <div className="flex items-center gap-3">
            <img src="/brand/idf.png" alt="International Diabetes Federation" className="h-7 w-auto object-contain" />
            <div className="leading-tight">
              <div className="text-[11px] uppercase tracking-wider text-slate-400">Endorsed by</div>
              <div className="font-semibold text-navy-900">International Diabetes Federation</div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section ref={statRef} className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s) => <StatCard key={s.label} {...s} active={statActive} />)}
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="section-pad gradient-soft">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div className="reveal">
            <span className="text-brand font-semibold tracking-[0.2em] uppercase text-sm">About DRS 2026</span>
            <h2 className="mt-3 font-display font-extrabold text-3xl md:text-4xl text-navy-900">Hello from #DRS2026!</h2>
            <div className="mt-4 w-20 h-1 bg-brand rounded-full" />
            <p className="mt-6 text-slate-600 leading-relaxed">
              We warmly invite you to the Congress that aims at improving lives of people with diabetes and obesity. DRS 2026
              brings together like-minded professionals to share expertise, inculcate research, and offer young physicians a
              platform to engage in scientific discussions.
            </p>
            <p className="mt-4 text-slate-600 leading-relaxed">
             With equal focus on cutting-edge advances and pragmatic, clinically relevant topics across diabetes, obesity and their overlap — plus the honour of hosting an International stream on Diabetes Complications curated by IDF.
            </p>
            <Link to="/about" className="mt-7 inline-flex items-center gap-2 text-brand font-semibold hover:gap-3 transition-all">
              Read More <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="reveal grid sm:grid-cols-2 gap-5">
            {[
              { icon: Icons.HeartPulse, t: "Diabesity Focus", d: "Equal emphasis on diabetes, obesity & their overlap." },
              { icon: Icons.Globe2, t: "Endorsed by IDF", d: "International Stream on Diabetes Complications Curated by IDF" },
              { icon: Icons.Microscope, t: "Cutting-edge Research", d: "Novel ideas, trials & pragmatic science." },
              { icon: Icons.Users, t: "Lived Voices", d: "Patient experiences shaping clinical practice." },
            ].map((f, i) => (
              <div key={i} className="card-hover bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
                <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center mb-4"><f.icon className="w-6 h-6 text-brand" /></div>
                <h3 className="font-display font-bold text-navy-900 text-lg">{f.t}</h3>
                <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* IDF BANNER */}
      <section className="bg-navy-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-15"><img src={mumbaiSkyline} alt="" className="w-full h-full object-cover" /></div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-14 flex flex-col md:flex-row items-center gap-6 justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-brand flex items-center justify-center shrink-0"><Globe2 className="w-7 h-7 text-white" /></div>
            <p className="text-white text-lg md:text-xl font-display font-medium max-w-2xl">{conference.idfNote}</p>
          </div>
          <Link to="/program" className="shrink-0 inline-flex items-center gap-2 bg-white text-navy-900 font-semibold rounded-full px-6 h-12 hover:bg-slate-100 transition-colors">
            View Program <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* COMMITTEE TEASER */}
      <section className="section-pad bg-white">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto reveal">
            <span className="text-brand font-semibold tracking-[0.2em] uppercase text-sm">Leadership</span>
            <h2 className="mt-3 font-display font-extrabold text-3xl md:text-4xl text-navy-900">Organising Committee</h2>
            <div className="mt-4 w-20 h-1 bg-brand rounded-full mx-auto" />
          </div>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-6">
            {committee.map((p) => (
              <div key={p.name} className="reveal group text-center">
                <div className="card-hover relative aspect-square rounded-2xl overflow-hidden bg-slate-100 border border-slate-100">
                  <img src={p.image} alt={p.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <h3 className="mt-3 font-display font-bold text-navy-900">{p.name}</h3>
                <p className="text-sm text-brand font-medium">{p.role}</p>
                {p.org && <p className="text-xs text-slate-500 mt-0.5">{p.org}</p>}
              </div>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link to="/committee" className="inline-flex items-center gap-2 text-brand font-semibold hover:gap-3 transition-all">View Full Committee <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      {/* TOPICS */}
      <section className="section-pad gradient-soft">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto reveal">
            <span className="text-brand font-semibold tracking-[0.2em] uppercase text-sm">Scientific Tracks</span>
            <h2 className="mt-3 font-display font-extrabold text-3xl md:text-4xl text-navy-900">What You Will Learn</h2>
            <div className="mt-4 w-20 h-1 bg-brand rounded-full mx-auto" />
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {topics.map((t) => {
              const Icon = Icons[t.icon] || Icons.Activity;
              return (
                <div key={t.title} className="reveal card-hover bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
                  <div className="w-12 h-12 rounded-xl bg-navy-800 flex items-center justify-center mb-4"><Icon className="w-6 h-6 text-white" /></div>
                  <h3 className="font-display font-bold text-navy-900 text-lg">{t.title}</h3>
                  <p className="mt-1.5 text-sm text-slate-500 leading-relaxed">{t.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* REGISTER CTA */}
      <section className="relative gradient-navy overflow-hidden">
        <div className="absolute -top-16 right-1/4 w-80 h-80 bg-brand/20 rounded-full blur-3xl" />
        <div className="relative max-w-5xl mx-auto px-5 lg:px-8 py-16 text-center">
          <h2 className="font-display font-extrabold text-3xl md:text-4xl text-white">Secure Your Place at DRS 2026</h2>
          <p className="mt-4 text-slate-300 max-w-2xl mx-auto">Early-bird registration is open for a limited time. Join 2,500+ healthcare professionals shaping the future of diabesity care.</p>
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <Link to="/registration" className="btn-shine inline-flex items-center bg-brand hover:bg-brand-600 text-white font-semibold rounded-full px-7 h-12 shadow-xl shadow-brand/30 transition-colors">Register Now <ArrowRight className="w-4 h-4 ml-2" /></Link>
            <Link to="/venue" className="inline-flex items-center rounded-full px-7 h-12 border border-white/30 text-white hover:bg-white/10 transition-colors">Explore Venue</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
